<ng-container *ngIf="viewModel$ | async as viewModel">
  <section class="p-mt-5">
    <header class="p-mb-3">
      <div class="p-d-flex gap-3">
        <h2>
          <i class="pi pi-map p-mr-1"></i>
          {{ viewModel.changeAreaState.header }}
        </h2>
        <p-tag
          *ngIf="viewModel.changeAreaState.showPendingTag"
          value="Pending"
          pTooltip="This job will be automatically reassigned to a new area after saving changes based on the selected start location"
          severity="warning"
        ></p-tag>
      </div>
      <p-button
        [pTooltip]="viewModel.changeAreaState.tooltip"
        [disabled]="viewModel.changeAreaState.disabled"
        [label]="viewModel.changeAreaState.buttonLabel"
        tooltipPosition="top"
        icon="pi pi-pencil"
        outlined="true"
        (onClick)="store.dispatch(actions.changeAreaButtonClicked())"
      ></p-button>
    </header>
    <ng-container *ngIf="!viewModel.jobLoading; else loading">
      <ng-container *ngIf="viewModel.hasLocations; else noLocations">
        <main class="locations-grid">
          <div class="left-column">
            <div class="locationMap">
              <app-travel-map></app-travel-map>
            </div>

            <div class="locations-menu">
              <button
                class="add-location-button"
                label="Location"
                pButton
                icon="pi pi-plus"
                outlined="true"
                [disabled]="viewModel.addLocationButtonDisabled"
                (click)="menu.toggle($event)"
              ></button>
              <ng-container
                *ngFor="
                  let location of viewModel.locations;
                  let last = last;
                  trackBy: trackByLocationType
                "
              >
                <app-overview-location-details
                  [locationType]="location.locationType"
                  [isActive]="
                    viewModel.activeLocationType === location.locationType
                  "
                  (locationClicked)="
                    store.dispatch(
                      actions.locationSelected({
                        locationType: location.locationType,
                      })
                    )
                  "
                ></app-overview-location-details>

                <div *ngIf="!last" class="location-separator">
                  <i class="pi pi-arrow-down"></i>
                  <span
                    *ngIf="!viewModel.canCalculateDistances"
                    class="distance-text"
                  >
                    Save changes to calculate distances.
                  </span>
                  <span
                    *ngIf="
                      viewModel.canCalculateDistances &&
                      viewModel.distancesLoading
                    "
                    class="distance-text"
                  >
                    <i class="pi pi-spin pi-spinner"></i> Calculating...
                  </span>
                  <span
                    *ngIf="
                      viewModel.canCalculateDistances &&
                      !viewModel.distancesLoading &&
                      location.distance
                    "
                    class="distance-text"
                  >
                    {{ location.distance.estimatedTime | secondsToTimeUnit }}
                    ({{
                      location.distance.distance
                        | distance: location.distance.distanceUnits
                    }})
                  </span>
                </div>
              </ng-container>
              <div
                *ngIf="viewModel.locations.length > 1"
                class="location-separator total-separator"
              >
                <i class="pi pi-stopwatch"></i>
                <span
                  *ngIf="!viewModel.canCalculateDistances"
                  class="distance-text"
                >
                  Save changes to calculate distances.
                </span>
                <span
                  *ngIf="
                    viewModel.canCalculateDistances &&
                    viewModel.distancesLoading
                  "
                  class="distance-text"
                >
                  <i class="pi pi-spin pi-spinner"></i> Calculating...
                </span>
                <span
                  *ngIf="
                    viewModel.canCalculateDistances &&
                    !viewModel.distancesLoading &&
                    !viewModel.totalDistance.locationsMissing
                  "
                  class="distance-text"
                >
                  Total (dock to dock):
                  {{
                    viewModel.totalDistance.estimatedTime | secondsToTimeUnit
                  }}
                  ({{
                    viewModel.totalDistance.distance
                      | distance: viewModel.totalDistance.distanceUnits
                  }})
                </span>
              </div>
            </div>
          </div>

          <div class="location-editor">
            <ng-container
              *ngIf="viewModel.activeLocationType; else noLocationSelected"
            >
              @defer (on viewport) {
                <app-street-view-map></app-street-view-map>
              } @placeholder {
                <p-skeleton
                  width="100%"
                  height="calc(45vh - 1.5rem)"
                ></p-skeleton>
              }
              <div class="editor-header">
                <h6>{{ viewModel.formattedLocationType | titlecase }}</h6>
                <div class="action-buttons">
                  <ng-container *ngIf="viewModel.view === 'form'">
                    <p-button
                      icon="pi pi-google-maps"
                      ariaLabel="Open in Google Maps"
                      [pTooltip]="viewModel.openInGoogleMapsButton.tooltip"
                      [text]="true"
                      (onClick)="
                        store.dispatch(
                          actions.openLocationInGoogleMapsButtonClicked()
                        )
                      "
                      [disabled]="viewModel.openInGoogleMapsButton.disabled"
                    />
                    <p-button
                      icon="pi pi-google-search"
                      ariaLabel="Search location in Google"
                      [pTooltip]="viewModel.openInGoogleSearchButton.tooltip"
                      [text]="true"
                      (onClick)="
                        store.dispatch(
                          actions.openLocationInGoogleButtonClicked()
                        )
                      "
                      [disabled]="viewModel.openInGoogleSearchButton.disabled"
                    />
                    <p-button
                      icon="pi pi-zillow"
                      ariaLabel="Open location in Zillow"
                      [pTooltip]="viewModel.openInZillowButton.tooltip"
                      [text]="true"
                      (onClick)="
                        store.dispatch(
                          actions.openLocationInZillowButtonClicked()
                        )
                      "
                      [disabled]="viewModel.openInZillowButton.disabled"
                    />
                    <p-button
                      icon="pi pi-ellipsis-v"
                      ariaLabel="Open location actions menu"
                      [text]="true"
                      (onClick)="actionsMenu.toggle($event)"
                      [disabled]="viewModel.jobNotInZone"
                    />
                    <p-menu
                      #actionsMenu
                      [model]="viewModel.actionsMenuItems"
                      [popup]="true"
                    />
                  </ng-container>
                  <p-button
                    *ngIf="viewModel.view === 'wizard'"
                    icon="pi pi-times"
                    ariaLabel="Exit Wizard"
                    (onClick)="
                      store.dispatch(actions.exitWizardButtonClicked())
                    "
                    [text]="true"
                  />
                </div>
              </div>
              <app-overview-location-editor
                *ngIf="viewModel.view === 'form'"
              ></app-overview-location-editor>
              <ng-container *ngIf="viewModel.view === 'wizard'">
                <span class="wizard-prompt">{{
                  viewModel.locationDetailsWizardPrompt
                }}</span>
                <app-location-details-wizard></app-location-details-wizard>
              </ng-container>
            </ng-container>
            <ng-template #noLocationSelected>
              <div class="no-location-selected">
                <i class="pi pi-info-circle"></i>
                <span>Select a location to view details</span>
              </div>
            </ng-template>
          </div>
        </main>
      </ng-container>
    </ng-container>
  </section>

  <ng-template #noLocations>
    <main class="p-mt-4 locations-empty">
      <div class="empty-state">
        <i class="pi pi-map"></i>
        <span
          >This job has no locations.
          <ng-container *ngIf="viewModel.showAddLocationLink">
            <a
              class="freya-link-button"
              (click)="store.dispatch(actions.addLocationLinkClicked())"
              >Add location</a
            >.
          </ng-container>
        </span>
      </div>
    </main>
  </ng-template>

  <ng-template #loading>
    <main class="p-mt-4">
      <p-skeleton
        class="flex-1"
        *ngFor="let _ of [1, 2]"
        width="100%"
        height="20rem"
      />
    </main>
  </ng-template>
  <p-menu
    #menu
    [popup]="true"
    appendTo="body"
    [model]="viewModel.addLocationOptions"
  ></p-menu>
</ng-container>
